import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box,
    Tab,
    Tabs,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Link,
    CircularProgress,
    TableSortLabel,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    useTheme,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { InfoOutlined, Clear as ClearIcon } from '@mui/icons-material';
import BarChartIcon from '@mui/icons-material/BarChart';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { RenderParticipantName } from './Elements';
import {
    fetchContestGroupings2,
    fetchContestRankings,
    fetchContestMetadata,
    RankingEntry,
    ContestField,
    ContestGrouping,
} from '../api/contests';
import { useLoadingCounter } from '../utils/useLoadingCounter';
import { SwipeHint } from '../utils/SwipeHint';
import Medal from '../utils/Medal';
import { Bar } from 'react-chartjs-2';
import useStyles from './useStyles';

import {
    Chart as ChartJS,
    BarElement,
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import SearchBar from './SearchBar'; // Import the SearchBar component

// Register Chart.js components
ChartJS.register(BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend);

const RankingView: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { contestId } = useParams<{ contestId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const { increment, decrement, isLoading } = useLoadingCounter();
    const classes = useStyles();

    const [groupData, setGroupData] = useState<ContestGrouping[]>([]);
    const [selectedGrouping, setSelectedGrouping] = useState<string | null>(null);
    const [rankingEntries, setRankingEntries] = useState<RankingEntry[]>([]);
    const [fields, setFields] = useState<ContestField[]>([]);
    const [searchText, setSearchText] = useState<string>('');
    const [sortField, setSortField] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [open, setOpen] = useState(false);
    const [participantData, setParticipantData] = useState<string>('');
    const [chartOpen, setChartOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const initialGrouping = useMemo(
        () => new URLSearchParams(location.search).get('group') || 'general',
        [location.search]
    );

    const groupings = useMemo(() => groupData?.map((g) => g.grouping) ?? [], [groupData]);
    const sortedGroupData = useMemo(() => groupData.sort((a, b) => b.group_count - a.group_count), [groupData]);

    const mainResultField = useMemo(
        () => fields.find((f) => f.main_result_field)?.field_name || 'result',
        [fields]
    );
    const mainResultLabel = useMemo(
        () => fields.find((f) => f.main_result_field)?.display_label || 'Результат',
        [fields]
    );
    const tableFields = useMemo(
        () => fields.filter((f) => f.field_name !== mainResultField),
        [fields, mainResultField]
    );

    useEffect(() => {
        if (contestId) {
            increment();
            fetchContestGroupings2(contestId)
                .then((data) => {
                    setGroupData(data);
                    const groups = data.map((d) => d.grouping);
                    if (!groups?.includes(initialGrouping)) {
                        setSelectedGrouping(groups[0] || 'general');
                    } else {
                        setSelectedGrouping(initialGrouping);
                    }
                })
                .catch((_) => setError('Ошибка при получении группировок'))
                .finally(decrement);

            increment();
            fetchContestMetadata(contestId)
                .then((data) => setFields(data))
                .catch((_) => setError('Ошибка при получении метаданных'))
                .finally(decrement);
        }
    }, [contestId, initialGrouping, increment, decrement]);

    useEffect(() => {
        if (selectedGrouping && contestId) {
            increment();
            fetchContestRankings(contestId, selectedGrouping)
                .then((data) => setRankingEntries(data))
                .catch((_) => setError('Ошибка при получении рейтингов'))
                .finally(decrement);
        }
    }, [selectedGrouping, contestId, increment, decrement]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const group = params.get('group');
        if (group && group !== selectedGrouping) {
            setSelectedGrouping(group);
        }
    }, [location.search, selectedGrouping]);

    const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
        const newGrouping = groupings[newValue];
        setSelectedGrouping(newGrouping);
        const params = new URLSearchParams(location.search);
        params.set('group', newGrouping);
        navigate({ search: params.toString() }, { replace: true });
    }, [groupings, location.search, navigate]);

    const handleSort = useCallback((field: string) => {
        const isAsc = sortField === field && sortDirection === 'asc';
        setSortField(field);
        setSortDirection(isAsc ? 'desc' : 'asc');
    }, [sortField, sortDirection]);

    const sortedEntries = useMemo(() => {
        return rankingEntries
            .filter((entry) =>
                entry.participant_name?.toLowerCase().includes(searchText.toLowerCase())
            )
            .sort((a, b) => {
                if (!sortField) return 0;
                const aValue = a[sortField];
                const bValue = b[sortField];
                if (aValue === bValue) return 0;
                return (aValue < bValue ? -1 : 1) * (sortDirection === 'asc' ? 1 : -1);
            });
    }, [rankingEntries, searchText, sortField, sortDirection]);

    const isDarkMode = theme.palette.mode === 'dark';
    const chartColors = {
        background: isDarkMode ? '#4caf50' : '#1976d2',
        text: isDarkMode ? '#ffffff' : '#000000',
        grid: isDarkMode ? '#666666' : '#cccccc',
    };

    const handleChartOpen = () => setChartOpen(true);
    const handleChartClose = () => setChartOpen(false);

    const chartData = {
        labels: sortedGroupData.map((group) => group.grouping),
        datasets: [
            {
                label: 'Публикаций',
                data: sortedGroupData.map((group) => group.group_count),
                backgroundColor: chartColors.background,
            },
        ],
    };

    const chartOptions: any = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (context: any) => `${context.raw} публ.`,
                },
            },
        },
        scales: {
            x: {
                title: { display: true, text: isMobile ? 'Публикаций' : 'Группы', color: chartColors.text },
                ticks: { color: chartColors.text },
                grid: { color: chartColors.grid },
                beginAtZero: true
            },
            y: {
                title: { display: true, text: isMobile ? 'Группы' : 'Публикаций' },
                ticks: { color: chartColors.text },
                grid: { color: chartColors.grid },
                beginAtZero: true },
        },
        indexAxis: isMobile ? 'y' : 'x',
    };

    const handleClearSearch = () => setSearchText('');
    const handleOpenParticipantData = (data: string) => {
        setParticipantData(data);
        setOpen(true);
    };
    const handleClose = () => setOpen(false);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: 'background.default', color: 'text.primary' }}>
            <Box display="flex" justifyContent="flex-end" mb={2}>
                <IconButton onClick={handleChartOpen} color="primary" title="График публикаций по группам" aria-label="Open chart">
                    <BarChartIcon />
                </IconButton>
            </Box>

            {groupings.length > 1 && (
                <Tabs
                    value={groupings.indexOf(selectedGrouping ?? '')}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile={true}
                    className={classes.stickyHeader}
                >
                    {groupings.map((grouping) => (
                        <Tab key={grouping} label={grouping} />
                    ))}
                </Tabs>
            )}

            <Box
                sx={{
                    padding: 2,
                    bgcolor: 'background.paper',
                    position: 'sticky',
                    top: 48,
                    zIndex: 9,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <SearchBar
                    searchText={searchText}
                    onSearchTextChange={setSearchText}
                    onClearSearch={handleClearSearch}
                />
            </Box>

            <SwipeHint />
            <Box className={classes.tableContainer}>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" mt={2}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box display="flex" justifyContent="center" mt={2}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                ) : (
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Участник</TableCell>
                                <TableCell align="left">
                                    <TableSortLabel
                                        active={sortField === 'ranking'}
                                        direction={sortField === 'ranking' ? sortDirection : 'asc'}
                                        onClick={() => handleSort('ranking')}
                                    >
                                        Место
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="left">
                                    <TableSortLabel
                                        active={sortField === mainResultField}
                                        direction={sortField === mainResultField ? sortDirection : 'asc'}
                                        onClick={() => handleSort(mainResultField)}
                                    >
                                        {mainResultLabel}
                                    </TableSortLabel>
                                </TableCell>
                                {tableFields.map((field) => (
                                    <TableCell key={field.field_name} align="left">
                                        <TableSortLabel
                                            active={sortField === field.field_name}
                                            direction={sortField === field.field_name ? sortDirection : 'asc'}
                                            onClick={() => handleSort(field.field_name)}
                                        >
                                            {field.display_label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell>Инфо</TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortField === "created_at"}
                                        direction={sortField === "created_at" ? sortDirection : 'asc'}
                                        onClick={() => handleSort("created_at")}
                                    >
                                        Дата
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>Видео</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedEntries.map((entry, index) => (
                                <TableRow key={index}>
                                    <TableCell>{RenderParticipantName(entry.participant_id, entry.participant_name, entry.profile_picture, true)}</TableCell>
                                    <TableCell align="left"><Medal ranking={entry.ranking} /></TableCell>
                                    <TableCell align="left">{entry[mainResultField]}</TableCell>
                                    {tableFields.map((field) => (
                                        <TableCell key={field.field_name} align="left">{entry[field.field_name]}</TableCell>
                                    ))}
                                    <TableCell>
                                        {entry.participant_data && (
                                            <IconButton onClick={() => handleOpenParticipantData(entry.participant_data)} size="small">
                                                <InfoOutlined fontSize="small" />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {entry.created_at && (
                                            <TableCell key={entry.created_at} align="left">{new Date(entry.created_at).toLocaleDateString('ru-RU')}</TableCell>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {entry.evidence_url && (
                                            <Link href={entry.evidence_url} target="_blank" rel="noopener noreferrer">
                                                Видео
                                            </Link>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Box>

            <Dialog open={chartOpen} onClose={handleChartClose} fullWidth maxWidth="md">
                <DialogTitle>Публикаций в группах</DialogTitle>
                <DialogContent>
                    <Box sx={{ height: isMobile ? 500 : 400, display: 'flex', justifyContent: 'center' }}>
                        <Bar data={chartData} options={chartOptions} />
                    </Box>
                </DialogContent>
            </Dialog>

            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Информация участника</DialogTitle>
                <DialogContent>
                    <Typography>{participantData}</Typography>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default RankingView;